// The list of addresses a tool's enquiries are emailed to: add (validated,
// de-duplicated, up to 5), remove, and CONFIRM. Enquiries only go to confirmed
// addresses, so adding one sends it a confirmation email (your own sign-in
// address is confirmed straight away). Used by the Notifications tab and the
// new-tool wizard; the parent owns the list and saves it, and reads the
// confirmation state from useRecipients().
import { useCallback, useEffect, useState, type FormEvent } from "react";
import { CheckCircle2, Clock, Plus, RotateCw, X } from "lucide-react";
import { useApi, type Recipient } from "../api";
import { config } from "../config";
import { Attention } from "./Attention";

export const MAX_EMAILS = 5; // matches MAX_NOTIFY_EMAILS in 100-api validation
const EMAIL = /^[^\s@]{1,64}@[^\s@]{1,190}\.[a-z]{2,}$/;
const POLL_MS = 5000; // re-check while any listed address is waiting

// What each enquiry email contains, shown so installers know what to expect
export const EMAIL_CONTENTS = [
  "The homeowner's name, phone number and email (reply to the email to reach them)",
  "The property address, its EPC rating and floor area",
  "Their answers about the home, marked where they changed what the EPC said",
  "The estimated heat loss and heat pump size",
  "The price range they were shown, after the Boiler Upgrade Scheme grant",
  "Which website it came from, when, and the consent they gave",
];

/** What a solar enquiry email contains (the wizard's Notifications step and tab). */
export const SOLAR_EMAIL_CONTENTS = [
  "The homeowner's name, phone number and email (reply to the email to reach them)",
  "The address and a map link to the roof",
  "The panels, system size, inverter and battery they chose, and the price range they were shown",
  "The generation, saving and usage they were shown, and how many panels fit on the roof",
  "Which website it came from, when, and the consent they gave",
];

export type Recipients = ReturnType<typeof useRecipients>;

/**
 * The company's confirmed / waiting addresses, kept fresh: re-checked every few
 * seconds while any address in `emails` is still waiting, so the page updates
 * by itself when someone clicks their confirmation link.
 */
export function useRecipients(emails: string[]) {
  const api = useApi();
  const [byEmail, setByEmail] = useState<Record<string, Recipient>>({});
  const [loaded, setLoaded] = useState(false);

  const refresh = useCallback(async () => {
    const list = await api.listRecipients();
    setByEmail(Object.fromEntries(list.map((r) => [r.email, r])));
    setLoaded(true);
  }, [api]);

  useEffect(() => {
    void refresh().catch(() => setLoaded(true));
  }, [refresh]);

  const waiting = emails.some((e) => byEmail[e]?.status !== "confirmed");
  useEffect(() => {
    if (!waiting) return;
    const t = window.setInterval(() => void refresh().catch(() => undefined), POLL_MS);
    return () => window.clearInterval(t);
  }, [waiting, refresh]);

  /** Add (or resend to) an address; returns its record. */
  const request = useCallback(
    async (email: string) => {
      const r = await api.addRecipient(email);
      setByEmail((m) => ({ ...m, [r.email]: r }));
      return r;
    },
    [api],
  );

  const isConfirmed = (email: string) => byEmail[email]?.status === "confirmed";
  return {
    loaded,
    byEmail,
    request,
    isConfirmed,
    allConfirmed: emails.length > 0 && emails.every(isConfirmed),
    anyConfirmed: emails.some(isConfirmed),
  };
}

export function NotifyEmailsEditor({
  emails,
  onChange,
  recipients,
  guide = false,
}: {
  emails: string[];
  onChange: (emails: string[]) => void;
  recipients: Recipients;
  guide?: boolean;
}) {
  const [input, setInput] = useState("");
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null); // address being added / resent

  async function add(e: FormEvent) {
    e.preventDefault();
    const addr = input.trim().toLowerCase();
    if (!EMAIL.test(addr)) return setError("Enter an email address like quotes@installer.co.uk");
    if (emails.includes(addr)) return setError(`${addr} is already on the list`);
    if (emails.length >= MAX_EMAILS) return setError(`Up to ${MAX_EMAILS} email addresses per component`);
    setBusy(addr);
    setError(null);
    try {
      // Confirmed already (or your own address)? Then it's ready; otherwise an email goes out
      if (!recipients.isConfirmed(addr)) await recipients.request(addr);
      onChange([...emails, addr]);
      setInput("");
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(null);
    }
  }

  async function resend(addr: string) {
    setBusy(addr);
    setError(null);
    try {
      await recipients.request(addr);
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(null);
    }
  }

  return (
    <>
      <form className="domain-add" onSubmit={(e) => void add(e)}>
        <span className="has-attention notify-input">
          <input
            className="input"
            type="email"
            placeholder="quotes@installer.co.uk"
            value={input}
            onChange={(e) => {
              setInput(e.target.value);
              setError(null);
            }}
            aria-label="Email address to notify"
          />
          {guide && emails.length === 0 && !input.trim() && <Attention corner="tr" label="Add the address enquiries should go to" />}
        </span>
        <span className="has-attention">
          <button type="submit" className="btn btn-secondary" disabled={!input.trim() || busy !== null}>
            <Plus size={16} aria-hidden="true" /> {busy && busy === input.trim().toLowerCase() ? "Sending…" : "Add"}
          </button>
          {guide && input.trim() && !busy && <Attention corner="tr" label="Add this address" />}
        </span>
      </form>
      {error && <p className="price-error">{error}</p>}
      {emails.length > 0 ? (
        <ul className="domain-list">
          {emails.map((a) => {
            const r = recipients.byEmail[a];
            const ok = r?.status === "confirmed";
            return (
              <li key={a}>
                <span className="notify-addr">
                  {a}
                  {/* Status: confirmed, or waiting for the link to be clicked */}
                  {ok ? (
                    <span className="notify-status is-ok" title={r?.method === "account" ? "Your own sign-in address" : "Confirmed"}>
                      <CheckCircle2 size={13} aria-hidden="true" /> Confirmed
                    </span>
                  ) : (
                    <span className="notify-status is-wait">
                      <Clock size={13} aria-hidden="true" /> {recipients.loaded ? "Waiting for confirmation" : "Checking…"}
                    </span>
                  )}
                </span>
                <span className="notify-actions">
                  {!ok && recipients.loaded && (
                    <button type="button" className="link-btn" onClick={() => void resend(a)} disabled={busy !== null}>
                      <RotateCw size={12} aria-hidden="true" /> {busy === a ? "Sending…" : "Resend"}
                    </button>
                  )}
                  <button type="button" className="icon-btn" aria-label={`Remove ${a}`} title="Remove" onClick={() => onChange(emails.filter((x) => x !== a))}>
                    <X size={14} />
                  </button>
                </span>
              </li>
            );
          })}
        </ul>
      ) : (
        <p className="muted domain-empty">No addresses yet: the calculator won't go live until it has a confirmed address to send enquiries to.</p>
      )}
      {/* Dev's SES account is in the sandbox: other addresses get AWS's own verification email */}
      {config.stage !== "prod" && (
        <p className="muted domain-empty">
          Test environment: the confirmation email for a new address comes from Amazon Web Services ("Email Address Verification Request"). Click its link to confirm.
        </p>
      )}
    </>
  );
}
